import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const PackageBuilder = ({
  packages,
  selectedPackage,
  selectedServices,
  services,
  countries,
  onPackageSelect,
  onSavePackage
}) => {
  const [formData, setFormData] = useState({
    packageName: '',
    packageCode: '',
    tier: 'silver',
    description: '',
    basePrice: '',
    discountPercentage: '0',
    finalPrice: '',
    includedUnits: '',
    additionalUnitPrice: '',
    status: 'active',
    countryId: ''
  });

  useEffect(() => {
    if (selectedPackage) {
      setFormData({
        packageName: selectedPackage?.packageName || '',
        packageCode: selectedPackage?.packageCode || '',
        tier: selectedPackage?.tier || 'silver',
        description: selectedPackage?.description || '',
        basePrice: selectedPackage?.basePrice?.toString() || '',
        discountPercentage: selectedPackage?.discountPercentage?.toString() || '0',
        finalPrice: selectedPackage?.finalPrice?.toString() || '',
        includedUnits: selectedPackage?.includedUnits?.toString() || '',
        additionalUnitPrice: selectedPackage?.additionalUnitPrice?.toString() || '',
        status: selectedPackage?.status || 'active',
        countryId: selectedPackage?.countryId || ''
      });
    }
  }, [selectedPackage]);

  useEffect(() => {
    if (formData?.basePrice && formData?.discountPercentage) {
      const base = parseFloat(formData?.basePrice);
      const discount = parseFloat(formData?.discountPercentage);
      if (!isNaN(base) && !isNaN(discount)) {
        const final = base - (base * discount / 100);
        setFormData(prev => ({ ...prev, finalPrice: final?.toFixed(2) }));
      }
    }
  }, [formData?.basePrice, formData?.discountPercentage]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    const basePrice = parseFloat(formData?.basePrice);
    const finalPrice = parseFloat(formData?.finalPrice);
    const includedUnits = parseInt(formData?.includedUnits);
    const discountPercentage = parseInt(formData?.discountPercentage);
    
    if (isNaN(basePrice) || basePrice <= 0) {
      alert('Veuillez entrer un prix de base valide');
      return;
    }
    
    if (isNaN(finalPrice) || finalPrice <= 0) {
      alert('Veuillez entrer un prix final valide');
      return;
    }
    
    if (isNaN(includedUnits) || includedUnits <= 0) {
      alert('Veuillez entrer un nombre d\'unités incluses valide');
      return;
    }
    
    const additionalUnitPrice = formData?.additionalUnitPrice 
      ? parseFloat(formData?.additionalUnitPrice) 
      : null;
    
    onSavePackage({
      ...formData,
      basePrice,
      discountPercentage,
      finalPrice,
      includedUnits,
      additionalUnitPrice,
      serviceIds: selectedServices
    });
  };

  const handleReset = () => {
    setFormData({
      packageName: '',
      packageCode: '',
      tier: 'silver',
      description: '',
      basePrice: '',
      discountPercentage: '0',
      finalPrice: '',
      includedUnits: '',
      additionalUnitPrice: '',
      status: 'active',
      countryId: ''
    });
    onPackageSelect(null);
  };

  const tierOptions = [
    { value: 'bronze', label: 'Bronze - Starter' },
    { value: 'silver', label: 'Silver - Standard' },
    { value: 'gold', label: 'Gold - Premium' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Actif' },
    { value: 'inactive', label: 'Inactif' },
    { value: 'archived', label: 'Archivé' }
  ];

  const countryOptions = [
    { value: '', label: 'Tous les pays' },
    ...countries?.map(country => ({
      value: country?.id,
      label: `${country?.countryName} (${country?.countryCode})`
    }))
  ];

  return (
    <div className="bg-card rounded-lg border border-border shadow-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Package" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">
              {selectedPackage ? 'Modifier le Package' : 'Créer un Package'}
            </h2>
            <p className="text-sm text-muted-foreground">
              Configurez les détails du package commercial
            </p>
          </div>
        </div>
        {selectedPackage && (
          <Button variant="outline" size="sm" iconName="X" onClick={handleReset}>
            Annuler
          </Button>
        )}
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground mb-2">
          Packages Existants
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {packages?.slice(0, 4)?.map(pkg => (
            <button
              key={pkg?.id}
              onClick={() => onPackageSelect(pkg)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                selectedPackage?.id === pkg?.id
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 bg-card'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-foreground">{pkg?.packageName}</h3>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  pkg?.tier === 'gold' ? 'bg-yellow-100 text-yellow-700' :
                  pkg?.tier === 'silver'? 'bg-gray-100 text-gray-700' : 'bg-orange-100 text-orange-700'
                }`}>
                  {pkg?.tier}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-2">{pkg?.packageCode}</p>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-primary">
                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })?.format(pkg?.finalPrice || 0)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {pkg?.services?.length || 0} services
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Nom du Package"
            value={formData?.packageName}
            onChange={(e) => setFormData(prev => ({ ...prev, packageName: e?.target?.value }))}
            placeholder="Ex: Pack Silver Orthodontie"
            required
          />
          <Input
            label="Code Package"
            value={formData?.packageCode}
            onChange={(e) => setFormData(prev => ({ ...prev, packageCode: e?.target?.value }))}
            placeholder="Ex: PKG-SLV-001"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Niveau (Tier)"
            value={formData?.tier}
            onChange={(e) => setFormData(prev => ({ ...prev, tier: e?.target?.value }))}
            options={tierOptions}
          />
          <Select
            label="Pays"
            value={formData?.countryId}
            onChange={(e) => setFormData(prev => ({ ...prev, countryId: e?.target?.value }))}
            options={countryOptions}
          />
        </div>

        <Input
          label="Description"
          value={formData?.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
          placeholder="Description du package"
          multiline
          rows={3}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Prix de Base (€)"
            type="number"
            step="0.01"
            value={formData?.basePrice}
            onChange={(e) => setFormData(prev => ({ ...prev, basePrice: e?.target?.value }))}
            placeholder="0.00"
            required
          />
          <Input
            label="Remise (%)"
            type="number"
            value={formData?.discountPercentage}
            onChange={(e) => setFormData(prev => ({ ...prev, discountPercentage: e?.target?.value }))}
            placeholder="0"
            min="0"
            max="100"
          />
          <Input
            label="Prix Final (€)"
            type="number"
            step="0.01"
            value={formData?.finalPrice}
            onChange={(e) => setFormData(prev => ({ ...prev, finalPrice: e?.target?.value }))}
            placeholder="0.00"
            required
            disabled
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Unités Incluses"
            type="number"
            value={formData?.includedUnits}
            onChange={(e) => setFormData(prev => ({ ...prev, includedUnits: e?.target?.value }))}
            placeholder="Ex: 10"
            required
          />
          <Input
            label="Prix Unitaire Additionnel (€)"
            type="number"
            step="0.01"
            value={formData?.additionalUnitPrice}
            onChange={(e) => setFormData(prev => ({ ...prev, additionalUnitPrice: e?.target?.value }))}
            placeholder="0.00"
          />
        </div>

        <Select
          label="Statut"
          value={formData?.status}
          onChange={(e) => setFormData(prev => ({ ...prev, status: e?.target?.value }))}
          options={statusOptions}
        />

        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="List" size={18} className="text-primary" />
            <span className="font-medium text-foreground">
              Services Sélectionnés: {selectedServices?.length}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Utilisez le panneau de droite pour sélectionner les services à inclure dans ce package
          </p>
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="submit" iconName="Save" fullWidth>
            {selectedPackage ? 'Mettre à Jour' : 'Créer le Package'}
          </Button>
          {selectedPackage && (
            <Button type="button" variant="outline" iconName="X" onClick={handleReset}>
              Annuler
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PackageBuilder;